import { trackSeoPerformanceMetrics } from "./analytics";

// Track web vitals for SEO performance
export const trackWebVitals = (): void => {
  if (typeof window !== "undefined" && "PerformanceObserver" in window) {
    // LCP (Largest Contentful Paint)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        const lcp = entry as PerformanceEntry;
        const value = lcp.startTime;

        // Store LCP value for later reporting
        window.__LCP_VALUE = value;

        // Check if we can already report
        tryReportWebVitals();
      });
    }).observe({ type: "largest-contentful-paint", buffered: true });

    // FID (First Input Delay)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === "first-input") {
          const fidEntry = entry as PerformanceEventTiming;
          const value = fidEntry.processingStart - fidEntry.startTime;

          // Store FID value for later reporting
          window.__FID_VALUE = value;

          // Check if we can already report
          tryReportWebVitals();
        }
      });
    }).observe({ type: "first-input", buffered: true });

    // CLS (Cumulative Layout Shift)
    let clsValue = 0;
    const clsEntries: PerformanceEntry[] = [];

    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();

      entries.forEach((entry) => {
        // Only count layout shifts without recent user input
        const layoutShiftEntry = entry as PerformanceEntry & {
          hadRecentInput?: boolean;
          value?: number;
        };
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value ?? 0;
          clsEntries.push(entry);

          // Store CLS value for later reporting
          window.__CLS_VALUE = clsValue;

          // Check if we can already report
          tryReportWebVitals();
        }
      });
    }).observe({ type: "layout-shift", buffered: true });

    // FCP (First Contentful Paint)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        if (entry.name === "first-contentful-paint") {
          const fcp = entry as PerformanceEntry;
          const value = fcp.startTime;

          // Store FCP value for later reporting
          window.__FCP_VALUE = value;

          // Check if we can already report
          tryReportWebVitals();
        }
      });
    }).observe({ type: "paint", buffered: true });

    // TTFB (Time to First Byte)
    const navigationEntries = performance.getEntriesByType("navigation");
    if (navigationEntries.length > 0) {
      const navEntry = navigationEntries[0] as PerformanceNavigationTiming;
      window.__TTFB_VALUE = navEntry.responseStart;

      // Check if we can already report
      tryReportWebVitals();
    }
  }
};

// Try to report web vitals if we have enough data
const tryReportWebVitals = () => {
  if (
    typeof window.__LCP_VALUE !== "undefined" &&
    typeof window.__FID_VALUE !== "undefined" &&
    typeof window.__CLS_VALUE !== "undefined" &&
    typeof window.__FCP_VALUE !== "undefined" &&
    typeof window.__TTFB_VALUE !== "undefined" &&
    !window.__VITALS_REPORTED
  ) {
    // Report vitals
    trackSeoPerformanceMetrics({
      lcp: window.__LCP_VALUE,
      fid: window.__FID_VALUE,
      cls: window.__CLS_VALUE,
      fcp: window.__FCP_VALUE,
      ttfb: window.__TTFB_VALUE,
      pageUrl: window.location.href,
    });

    // Mark as reported
    window.__VITALS_REPORTED = true;
  }
};

// Add types for global variables
declare global {
  interface Window {
    __LCP_VALUE?: number;
    __FID_VALUE?: number;
    __CLS_VALUE?: number;
    __FCP_VALUE?: number;
    __TTFB_VALUE?: number;
    __VITALS_REPORTED?: boolean;
  }
}
